// CareLogix™ ORACLE — AdminOne Swarm Approval Queue (Phase 32A)
import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PALETTE } from "@/components/animation/MotionShared";
import { adminSwarmApi } from "@/api/swarmClient";

export default function AdminSwarmCommandPanel({ testid = "p32-admin-swarm-command" }) {
  const [items, setItems] = useState([]);
  const [resolved, setResolved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await adminSwarmApi.queue({ resolved: resolved, limit: 50 });
      setItems(r.data?.items || []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setError("AdminOne authentication required.");
      } else {
        setError("Could not load swarm queue.");
      }
    } finally {
      setLoading(false);
    }
  }, [resolved]);

  useEffect(() => { load(); }, [load]);

  const act = useCallback(async (trace_id, action) => {
    setActing(trace_id);
    try {
      await adminSwarmApi.resolve(trace_id, action);
      await load();
    } catch (_e) {
      // ignore
    } finally {
      setActing(null);
    }
  }, [load]);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.header}>
        <div>
          <h3 style={S.title} data-testid={`${testid}-title`}>Swarm Approval Queue™</h3>
          <p style={S.subtitle}>Human-in-the-loop approval for high-risk swarm traces. Approve, reject, or escalate.</p>
        </div>
        <div style={S.tabs}>
          <button
            type="button"
            onClick={() => setResolved(false)}
            style={{ ...S.tab, ...(!resolved ? S.tabActive : null) }}
            data-testid={`${testid}-tab-pending`}
          >
            Pending
          </button>
          <button
            type="button"
            onClick={() => setResolved(true)}
            style={{ ...S.tab, ...(resolved ? S.tabActive : null) }}
            data-testid={`${testid}-tab-resolved`}
          >
            Resolved
          </button>
          <button type="button" onClick={load} style={S.refresh} data-testid={`${testid}-refresh`}>Refresh</button>
        </div>
      </header>

      {error && <div style={S.err} data-testid={`${testid}-error`}>{error}</div>}
      {loading && <div style={S.loading}>Loading queue…</div>}

      {!loading && !error && items.length === 0 && (
        <div style={S.empty} data-testid={`${testid}-empty`}>
          {resolved ? "No resolved items." : "No pending items. The Swarm is calm."}
        </div>
      )}

      <ul style={S.list}>
        {items.map((it) => (
          <motion.li
            key={it.trace_id}
            style={S.item}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid={`${testid}-item-${it.trace_id}`}
          >
            <div style={S.itemHead}>
              <span style={S.itemTopic} data-testid={`${testid}-item-${it.trace_id}-topic`}>{it.topic}</span>
              <span style={{ ...S.itemVerdict, color: PALETTE.amber, borderColor: PALETTE.amber }}>{(it.verdict || "—").toUpperCase()}</span>
            </div>
            <div style={S.itemMeta}>
              <span>Risk: <strong style={{ color: it.risk_score >= 70 ? PALETTE.red : PALETTE.slate3 }}>{it.risk_score}</strong></span>
              <span>Created: {it.created_at}</span>
              {resolved && <span>Resolution: <strong style={{ color: PALETTE.greenAccent }}>{it.resolution || "—"}</strong></span>}
              {resolved && it.resolved_by && <span>By: {it.resolved_by}</span>}
            </div>
            {!resolved && (
              <div style={S.actions}>
                <button
                  type="button"
                  onClick={() => act(it.trace_id, "approve")}
                  disabled={acting === it.trace_id}
                  style={{ ...S.actBtn, ...S.actApprove }}
                  data-testid={`${testid}-item-${it.trace_id}-approve`}
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => act(it.trace_id, "reject")}
                  disabled={acting === it.trace_id}
                  style={{ ...S.actBtn, ...S.actReject }}
                  data-testid={`${testid}-item-${it.trace_id}-reject`}
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={() => act(it.trace_id, "escalate")}
                  disabled={acting === it.trace_id}
                  style={{ ...S.actBtn, ...S.actEscalate }}
                  data-testid={`${testid}-item-${it.trace_id}-escalate`}
                >
                  Escalate
                </button>
              </div>
            )}
          </motion.li>
        ))}
      </ul>

      <footer style={S.foot} data-testid={`${testid}-foot`}>
        HITL governance. All decisions logged for audit.
      </footer>
    </section>
  );
}

const S = {
  shell:        { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 18, display: "flex", flexDirection: "column", gap: 12, color: PALETTE.slate3 },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" },
  title:        { margin: 0, color: PALETTE.goldBright, fontSize: 16, letterSpacing: "0.05em" },
  subtitle:     { margin: "4px 0 0 0", color: PALETTE.slate4, fontSize: 12, lineHeight: 1.4 },
  tabs:         { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
  tab:          { appearance: "none", background: "transparent", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.slate3, padding: "5px 11px", borderRadius: 999, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.06em" },
  tabActive:    { background: PALETTE.gold, color: PALETTE.bgDeep, borderColor: PALETTE.gold, fontWeight: 800 },
  refresh:      { appearance: "none", background: "transparent", border: `1px dashed ${PALETTE.goldGlow}`, color: PALETTE.gold, padding: "5px 11px", borderRadius: 999, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.06em" },
  err:          { color: PALETTE.red, fontSize: 12.5, background: "rgba(239,68,68,0.06)", border: `1px solid ${PALETTE.red}`, padding: "6px 10px", borderRadius: 8 },
  loading:      { color: PALETTE.slate4, fontSize: 12.5 },
  empty:        { color: PALETTE.slate4, fontSize: 13, padding: 12, textAlign: "center", border: "1px dashed rgba(255,255,255,0.08)", borderRadius: 10 },
  list:         { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 },
  item:         { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 8 },
  itemHead:     { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  itemTopic:    { color: PALETTE.goldBright, fontSize: 13, fontWeight: 700 },
  itemVerdict:  { fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", padding: "3px 8px", borderRadius: 999, border: "1px solid" },
  itemMeta:     { display: "flex", gap: 12, flexWrap: "wrap", color: PALETTE.slate4, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.04em" },
  actions:      { display: "flex", gap: 6, flexWrap: "wrap" },
  actBtn:       { appearance: "none", padding: "5px 11px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", cursor: "pointer" },
  actApprove:   { background: PALETTE.greenAccent, color: PALETTE.bgDeep, border: "none" },
  actReject:    { background: "transparent", color: PALETTE.red, border: `1px solid ${PALETTE.red}` },
  actEscalate:  { background: "transparent", color: PALETTE.amber, border: `1px solid ${PALETTE.amber}` },
  foot:         { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5, textAlign: "right" },
};
